import type { ReactNode } from "react";
import { AbilityProvider, Can as CaslCan, useAbility } from "@casl/react";
import type { CanProps } from "@casl/react";
import {
  createMongoAbility,
  subject as caslSubject,
  type RawRuleOf,
} from "@casl/ability";
import { unpackRules, type PackRule } from "@casl/ability/extra";
import type { AppAbility, ProjectSubject } from "./types";

// Build an ability from the packed rules shipped on the user payload.
export const buildAbility = (rules: unknown): AppAbility => {
  const packed = (Array.isArray(rules) ? rules : []) as PackRule<
    RawRuleOf<AppAbility>
  >[];
  return createMongoAbility<AppAbility>(unpackRules(packed));
};

// Tag a plain project object so CASL detects the 'Project' subject type. Always
// use this when checking — a raw entity would be detected by its constructor.
export const projectSubject = (project: ProjectSubject) =>
  caslSubject("Project", {
    id: project.id,
    organizationId: project.organizationId ?? null,
  });

export { AbilityProvider };
export const useAppAbility = (): AppAbility => useAbility<AppAbility>();

// @casl/react v7's <Can> is generic and reads the ability from <AbilityProvider>.
// Re-export it pre-bound to AppAbility so I / this / field are type-checked. Pass
// per-project rules via the `this` prop (a="Project" only checks the type, which
// ignores the {id, organizationId} conditions).
export const Can: (props: CanProps<AppAbility>) => ReactNode = CaslCan;
